class VariantSelects extends HTMLElement {
    constructor() {
        super();
        this.item = $(this).closest('.productView');
        this.isFullWidth = this.item.is('.layout-4');
        this.variantCache = {};
        this._highVariantRequestId = 0;
        this._sectionAbortController = null;
        this.variantDataPromise = this.loadVariantData();

        this.variantDataPromise.then(() => {
            this.onVariantInit();
        });
        this.addEventListener('change', this.onVariantChange.bind(this));

        if (this.shouldUseSectionRendering()) {
            this.addEventListener('pointerenter', this.onOptionPointerEnter.bind(this), true);
        }
    }

    onOptionPointerEnter(event) {
        let input = event.target.closest?.('input[data-option-value-id]');
        if (!input) {
            const label = event.target.closest?.('label[for]');
            if (label) input = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
        }
        if (!input || input.checked) return;

        const fieldsets = this.querySelectorAll('.optionValueSelectors fieldset');
        if (!fieldsets.length) return;

        const optionValueIds = Array.from(fieldsets).map((fieldset) => {
            if (fieldset.contains(input)) return input.dataset.optionValueId;
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            return checked?.dataset?.optionValueId;
        }).filter(Boolean);

        if (!optionValueIds.length) return;

        const productUrl = input.dataset?.productUrl || this.dataset.url || window.location.pathname;
        VariantSelects.fetchOptionPickerSection(
            productUrl,
            this.dataset.section,
            optionValueIds
        ).catch(() => {});
    }

    static sectionCache = new Map();
    static sectionInflight = new Map();
    static SECTION_CACHE_MAX = 48;

    static getSectionCacheKey(sectionId, optionValueIds) {
        return `${sectionId}|${optionValueIds.join(',')}`;
    }

    static cacheSectionHtml(key, html) {
        if (VariantSelects.sectionCache.size >= VariantSelects.SECTION_CACHE_MAX) {
            const firstKey = VariantSelects.sectionCache.keys().next().value;
            VariantSelects.sectionCache.delete(firstKey);
        }
        VariantSelects.sectionCache.set(key, html);
    }

    static fetchOptionPickerSection(productUrl, sectionId, optionValueIds, signal) {
        const key = VariantSelects.getSectionCacheKey(sectionId, optionValueIds);

        if (VariantSelects.sectionCache.has(key)) {
            return Promise.resolve(VariantSelects.sectionCache.get(key));
        }

        if (VariantSelects.sectionInflight.has(key)) {
            return VariantSelects.sectionInflight.get(key);
        }

        const params = optionValueIds.length > 0 ? `&option_values=${optionValueIds.join(',')}` : '';
        const joiner = productUrl.includes('?') ? '&' : '?';

        const promise = fetch(`${productUrl}${joiner}section_id=${sectionId}${params}`, { signal })
            .then((response) => {
                if (!response.ok) throw new Error('section render failed');
                return response.text();
            })
            .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                VariantSelects.cacheSectionHtml(key, html);
                VariantSelects.sectionInflight.delete(key);
                return html;
            })
            .catch((error) => {
                VariantSelects.sectionInflight.delete(key);
                throw error;
            });

        VariantSelects.sectionInflight.set(key, promise);
        return promise;
    }

    static applyOptionPickersFromHtml(html, productId) {
        ['main', 'sticky'].forEach((pickerType) => {
            const sourcePicker = html.querySelector(`[data-option-picker="${pickerType}"] .optionValueSelectors`);
            if (!sourcePicker) return;

            document.querySelectorAll(`[data-option-picker="${pickerType}"][data-product="${productId}"]`).forEach((pickerEl) => {
                const targetPicker = pickerEl.querySelector('.optionValueSelectors');
                if (targetPicker) targetPicker.innerHTML = sourcePicker.innerHTML;
            });
        });
    }

    static getEventOptionInput(pickerEl, event) {
        let input = event.target.closest?.('input[data-option-value-id]');
        if (!input) {
            const label = event.target.closest?.('label[for]');
            if (label) input = pickerEl.querySelector(`#${CSS.escape(label.htmlFor)}`);
        }
        return input;
    }

    static getChangedOptionFieldsetIndex(pickerEl, event) {
        const input = VariantSelects.getEventOptionInput(pickerEl, event);
        if (!input) return -1;

        const fieldsets = [...pickerEl.querySelectorAll('.optionValueSelectors fieldset')];
        const fieldset = input.closest('fieldset');
        return fieldsets.indexOf(fieldset);
    }

    static canUseFastPath(pickerEl, event) {
        const fieldsets = pickerEl.querySelectorAll('.optionValueSelectors fieldset');
        if (fieldsets.length < 2) return false;

        const changedIndex = VariantSelects.getChangedOptionFieldsetIndex(pickerEl, event);
        if (changedIndex !== fieldsets.length - 1) return false;

        const allSelected = Array.from(fieldsets).every((fieldset) => (
            fieldset.querySelector('input[type="radio"]:checked')
        ));
        if (!allSelected) return false;

        return Boolean(pickerEl.getSelectedVariantIdFromDom?.());
    }

    static syncAllPickersByOptionIds(productId, clickedIds) {
        document.querySelectorAll(
            `variant-radios[data-product="${productId}"], variant-sticky-radios[data-product="${productId}"]`
        ).forEach((picker) => {
            picker.restoreSelectedOptionValues?.(clickedIds);
            picker.updateOptionHeaders?.();
        });
    }

    loadVariantData() {
        // Liquid and /products/{handle}.js both cap at 250 variants.
        // Products with >250 variants rely on Section Rendering API + /variants/{id}.js.
        return Promise.resolve(this.getVariantDataSync());
    }

    populateInventoryArrays(product) {
        const invenKey = `product_inven_array_${product.id}`;
        const sellingKey = `selling_array_${product.id}`;

        window[invenKey] = window[invenKey] || {};
        window[sellingKey] = window[sellingKey] || {};

        product.variants.forEach((variant) => {
            window[invenKey][variant.id] = String(variant.inventory_quantity);
            window[sellingKey][variant.id] = variant.inventory_policy;
        });
    }

    getVariantDataSync() {
        if (!this.variantData) {
            const jsonEl = this.querySelector('[type="application/json"]');
            this.variantData = jsonEl ? JSON.parse(jsonEl.textContent) : [];
        }
        return this.variantData;
    }

    shouldUseSectionRendering() {
        return parseInt(this.dataset.variantsCount || '0', 10) > 250;
    }

    getSelectedVariantIdFromDom() {
        const container = this.querySelector('.optionValueSelectors');
        if (!container) return null;

        const fieldsets = container.querySelectorAll('fieldset');
        if (!fieldsets.length) return null;

        const allSelected = Array.from(fieldsets).every((fieldset) => (
            fieldset.querySelector('input[type="radio"]:checked')
        ));
        if (!allSelected) return null;

        const lastChecked = fieldsets[fieldsets.length - 1].querySelector('input[type="radio"]:checked');
        return lastChecked?.dataset?.variantId || null;
    }

    cacheVariantInventory(variant) {
        if (!variant?.id) return;

        const invenKey = `product_inven_array_${this.dataset.product}`;
        const sellingKey = `selling_array_${this.dataset.product}`;

        window[invenKey] = window[invenKey] || {};
        window[sellingKey] = window[sellingKey] || {};
        window[invenKey][variant.id] = String(variant.inventory_quantity);
        window[sellingKey][variant.id] = variant.inventory_policy;
    }

    ensureVariantMedia(variant) {
        if (!variant) return variant;
        if (!variant.featured_media && variant.featured_image?.id) {
            variant.featured_media = { id: variant.featured_image.id };
        }
        return variant;
    }

    resolveCurrentVariantFromDom() {
        const variantId = this.getSelectedVariantIdFromDom();
        if (!variantId) {
            this.currentVariant = null;
            return Promise.resolve();
        }

        const localVariant = this.getVariantData().find((variant) => String(variant.id) === String(variantId));
        if (localVariant) {
            this.currentVariant = this.ensureVariantMedia(localVariant);
            this.cacheVariantInventory(localVariant);
            return Promise.resolve();
        }

        if (this.variantCache[variantId]) {
            this.currentVariant = this.ensureVariantMedia(this.variantCache[variantId]);
            return Promise.resolve();
        }

        const root = window.Shopify?.routes?.root || '/';
        return fetch(`${root}variants/${variantId}.js`)
            .then((response) => {
                if (!response.ok) throw new Error('variant fetch failed');
                return response.json();
            })
            .then((variant) => {
                this.ensureVariantMedia(variant);
                this.variantCache[variantId] = variant;
                this.currentVariant = variant;
                this.cacheVariantInventory(variant);
            })
            .catch(() => {
                this.currentVariant = null;
            });
    }

    getVariantOptions(variant) {
        if (variant.options?.length) return variant.options;
        return [variant.option1, variant.option2, variant.option3].filter((option) => option != null && option !== '');
    }

    updateMasterId() {
        this.decodeOptions();
        this.currentVariant = this.getVariantData().find((variant) => {
            const variantOptions = this.getVariantOptions(variant);
            if (variantOptions.length !== this.options.length) return false;
            return variantOptions.every((option, index) => this.options[index] === option);
        });
        if (this.currentVariant) {
            this.ensureVariantMedia(this.currentVariant);
        }
    }

    getSelectedOptionValueIds() {
        const container = this.querySelector('.optionValueSelectors');
        if (!container) return [];

        const checkedRadios = container.querySelectorAll('input[type="radio"]:checked');
        if (checkedRadios.length) {
            return Array.from(checkedRadios).map((input) => input.dataset.optionValueId).filter(Boolean);
        }

        const selects = container.querySelectorAll('select');
        if (selects.length) {
            return Array.from(selects).map((select) => {
                const selectedOption = select.options[select.selectedIndex];
                return selectedOption?.dataset?.optionValueId;
            }).filter(Boolean);
        }

        return [];
    }

    ensureOptionInputChecked(event) {
        const label = event.target.closest('label[for]');
        if (!label) return;

        const input = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
        if (input?.type === 'radio') {
            input.checked = true;
        }
    }

    restoreSelectedOptionValues(optionValueIds) {
        const container = this.querySelector('.optionValueSelectors');
        if (!container || !optionValueIds?.length) return;

        optionValueIds.forEach((id) => {
            const radio = container.querySelector(`input[data-option-value-id="${id}"]`);
            if (radio) radio.checked = true;

            const option = container.querySelector(`option[data-option-value-id="${id}"]`);
            if (option) option.selected = true;
        });
    }

    updateOptionHeaders() {
        const container = this.querySelector('.optionValueSelectors');
        if (!container) return;

        container.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((wrapper) => {
            const header = wrapper.querySelector('[data-header-option]');
            const checked = wrapper.querySelector('input[type="radio"]:checked');
            const select = wrapper.querySelector('select');

            if (header && checked) header.textContent = this.decodeOption(checked.value);
            if (header && select) header.textContent = this.decodeOption(select.value);
        });
    }

    handleHighVariantChange(event) {
        this.ensureOptionInputChecked(event);
        const clickedIds = this.getSelectedOptionValueIds();
        if (!clickedIds.length) return Promise.resolve();

        this.updateOptionHeaders();

        if (VariantSelects.canUseFastPath(this, event)) {
            return this.handleHighVariantChangeFastPath(event, clickedIds);
        }

        if (this.isLastOptionFieldsetTarget(event)) {
            this.syncUrlFromDom();
        }

        if (this._sectionAbortController) {
            this._sectionAbortController.abort();
        }
        this._sectionAbortController = new AbortController();

        const requestId = ++this._highVariantRequestId;
        const focusId = event.target?.id;
        const productUrl = event.target?.dataset?.productUrl || this.dataset.url || window.location.pathname;
        const sectionId = this.dataset.section;

        return VariantSelects.fetchOptionPickerSection(
            productUrl,
            sectionId,
            clickedIds,
            this._sectionAbortController.signal
        )
            .then((html) => {
                if (requestId !== this._highVariantRequestId) return;

                VariantSelects.applyOptionPickersFromHtml(html, this.dataset.product);
                this.restoreSelectedOptionValues(clickedIds);
                this.updateOptionHeaders();
                this.syncUrlFromDom();

                if (focusId) {
                    this.querySelector(`#${CSS.escape(focusId)}`)?.focus();
                }

                this.prefetchAlternateOptions();

                return this.runHighVariantUpdates(false);
            })
            .catch((error) => {
                if (error?.name === 'AbortError') return;
                console.warn('Section Rendering API failed:', error);
            });
    }

    handleHighVariantChangeFastPath(event, clickedIds) {
        VariantSelects.syncAllPickersByOptionIds(this.dataset.product, clickedIds);
        this.syncUrlFromDom();

        const focusId = event.target?.id;
        if (focusId) {
            this.querySelector(`#${CSS.escape(focusId)}`)?.focus();
        }

        return this.runHighVariantUpdates(false, { skipSectionPriceFetch: true });
    }

    prefetchAlternateOptions() {
        const fieldsets = this.querySelectorAll('.optionValueSelectors fieldset');
        if (!fieldsets.length) return;

        const productUrl = this.dataset.url || window.location.pathname;
        const sectionId = this.dataset.section;
        const defer = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));

        const prefetchWithOverride = (fieldsetIndex, radio) => {
            const ids = Array.from(fieldsets).map((fieldset, index) => {
                if (index === fieldsetIndex) return radio.dataset.optionValueId;
                return fieldset.querySelector('input[type="radio"]:checked')?.dataset?.optionValueId;
            }).filter(Boolean);

            if (ids.length !== fieldsets.length) return;

            VariantSelects.fetchOptionPickerSection(productUrl, sectionId, ids).catch(() => {});
        };

        defer(() => {
            fieldsets[0]?.querySelectorAll('input[data-option-value-id]').forEach((radio) => {
                prefetchWithOverride(0, radio);
            });

            if (fieldsets.length > 2) {
                fieldsets[1]?.querySelectorAll('input[data-option-value-id]').forEach((radio) => {
                    prefetchWithOverride(1, radio);
                });

                const lastIndex = fieldsets.length - 1;
                fieldsets[lastIndex]?.querySelectorAll('input[data-option-value-id]').forEach((radio) => {
                    prefetchWithOverride(lastIndex, radio);
                });
            }
        });
    }

    renderProductOptionValues(targetElement, optionValueIds) {
        const sectionId = this.dataset.section;
        const selectedOptionValues = optionValueIds || this.getSelectedOptionValueIds();
        const productUrl = targetElement?.dataset?.productUrl || this.dataset.url || window.location.pathname;

        return VariantSelects.fetchOptionPickerSection(productUrl, sectionId, selectedOptionValues)
            .then((html) => {
                VariantSelects.applyOptionPickersFromHtml(html, this.dataset.product);
                return html;
            })
            .catch((error) => {
                console.warn('Section Rendering API failed:', error);
            });
    }

    syncStickyOptionPickers() {
        if (this.dataset.optionPicker !== 'main' || this.shouldUseSectionRendering()) return;
        if (!this.options?.length) return;

        document.querySelectorAll(`variant-sticky-radios[data-product="${this.dataset.product}"]`).forEach((stickyPicker) => {
            const container = stickyPicker.querySelector('.optionValueSelectors');
            container?.querySelectorAll('fieldset').forEach((fieldset, index) => {
                const value = this.options[index];
                if (!value) return;

                fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
                    radio.checked = this.decodeOption(radio.value) === value;
                });
            });

            stickyPicker.variantDataPromise.then(() => {
                stickyPicker.updateOptions();
                stickyPicker.updateMasterId();
                stickyPicker.updateSelectVariant?.();
            });
        });
    }

    runHighVariantUpdates(unavailable, options = {}) {
        const { skipSectionPriceFetch = false } = options;
        this.updateOptions();
        return this.resolveCurrentVariantFromDom().then(() => {
            if (unavailable) {
                this.updateAttribute(true);
                this.updateStickyAddToCart(true);
                return;
            }

            if (!this.currentVariant) {
                this.updateOptionHeaders();
                this.updateAttribute(true);
                this.updateStickyAddToCart(true);
                return;
            }

            this.updateOptionHeaders();
            this.updateVariantInput();
            this.updateURL();
            this.updateAttribute(false, !this.currentVariant.available);
            this.updateStickyAddToCart(false, !this.currentVariant.available);
            this.renderProductInfo();
            this.checkQuantityWhenVariantChange();
            this.updateMedia(0);

            if (skipSectionPriceFetch) {
                this.updatePriceFromVariant();
            } else {
                requestAnimationFrame(() => {
                    this.renderProductAjaxInfo();
                });
            }

            const deferHeavy = window.requestIdleCallback || ((cb) => setTimeout(cb, 150));
            deferHeavy(() => {
                this.updatePickupAvailability();
            });
        });
    }

    runVariantUpdates(unavailable) {
        this.updateOptions();
        this.updateMasterId();
        this.updatePickupAvailability();

        if (!this.shouldUseSectionRendering()) {
            this.updateVariantStatuses();
        }

        if (unavailable) {
            this.updateAttribute(true);
            this.updateStickyAddToCart(true);
            return;
        }

        if (!this.currentVariant) {
            this.updateOptionHeaders();
            this.updateAttribute(true);
            this.updateStickyAddToCart(true);
            return;
        }

        this.updateOptionHeaders();
        this.updateMedia(200);
        this.updateURL();
        this.updateVariantInput();
        this.renderProductAjaxInfo();
        this.renderProductInfo();
        this.updateProductInfo();
        this.updateAttribute(false, !this.currentVariant.available);
        this.updateStickyAddToCart(false, !this.currentVariant.available);
        this.checkQuantityWhenVariantChange();
    }

    onVariantInit(){
        if (this.shouldUseSectionRendering()) {
            this.updateOptions();
            return this.resolveCurrentVariantFromDom().then(() => {
                this.updateMedia(1500);
                if (this.currentVariant) {
                    this.renderProductAjaxInfo();
                    this.renderProductInfo();
                }
                if (!this.currentVariant) {
                    this.updateAttribute(true);
                } else {
                    this.updateAttribute(false, !this.currentVariant.available);
                }
                this.prefetchAlternateOptions();
            });
        }

        this.updateOptions();
        this.updateMasterId();
        this.updateMedia(1500);
        // this.updateURL();
        this.renderProductAjaxInfo();
        this.renderProductInfo();
        if (!this.currentVariant) {
            this.updateAttribute(true);
        } else {
            this.updateAttribute(false, !this.currentVariant.available);
        }

        this.updateVariantStatuses();
    }
    
    onVariantChange(event) {
        if (this.shouldUseSectionRendering()) {
            this.handleHighVariantChange(event);
            return;
        }

        this.variantDataPromise.then(() => {
            this.runVariantUpdates(false);
        });
    }

    updateOptions() {
        this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
    }

    decodeOptions() {
        this.options = this.options.map(option => {
            const parsedOption = this.decodeOption(option)
            return parsedOption
        })
    }
  
    decodeOption(option) {
      if (option) {
          return option.split('Special_Double_Quote').join('"').split('Special_Slash').join('/')
        } else {
          return null
        }
    }

    encodeOption(option) {
        if (option) {
          return option.split('"').join('Special_Double_Quote').split('/').join('Special_Slash')
        } else {
          return null
        }
    }
      
    updateMedia(time) {
        if (!this.currentVariant) return;

        this.ensureVariantMedia(this.currentVariant);
        const mediaId = this.currentVariant.featured_media?.id;
        if (!mediaId) return;

        const newMedia = document.querySelectorAll(
            `[data-media-id="${this.dataset.section}-${mediaId}"]`
        );

        if (!newMedia.length) return;
        window.setTimeout(() => {
            $(newMedia).trigger('click');
        }, time);

        if (!this.isFullWidth || window.innerWidth < 768) return;

        const mediaToReplace = document.querySelector('.productView-image[data-index="1"]')
        const imageToReplace = mediaToReplace.querySelector('img')

        if (!this.currentVariant) return;

        const image = this.currentVariant?.featured_image;

        if (image == null) return;

        imageToReplace.setAttribute('src',  image.src)
        imageToReplace.setAttribute('srcset', image.src)
        imageToReplace.setAttribute('alt', image.alt)

        if (mediaToReplace.getBoundingClientRect().top < window.scrollY) {
            this.scrollToBlock(mediaToReplace)
        }
    }

    scrollToBlock(block) {
        const headerHeight = document.querySelectorAll('.section-header-navigation')[0]?.getBoundingClientRect().height 
        const announcementBarHeight = document.querySelectorAll('.announcement-bar')[0]?.getBoundingClientRect().height 
        const positionTop = block.getBoundingClientRect().top - headerHeight - announcementBarHeight 

        window.scrollTo({
            top: positionTop,
            behavior: 'smooth'
        })
    }

    updateURL() {
        if (!this.currentVariant) return;
        this.updateURLWithVariantId(this.currentVariant.id);
    }

    updateURLWithVariantId(variantId) {
        if (!variantId) return;
        const nextUrl = `${this.dataset.url}?variant=${variantId}`;
        if (window.location.pathname + window.location.search === nextUrl) return;
        window.history.replaceState({}, '', nextUrl);
    }

    updateVariantInputWithId(variantId) {
        if (!variantId) return;

        document.querySelectorAll(
            `#product-form-${this.dataset.product}, #product-form-installment-${this.dataset.product}`
        ).forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            if (!input) return;
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    isLastOptionFieldsetTarget(event) {
        const fieldsets = this.querySelectorAll('.optionValueSelectors fieldset');
        if (!fieldsets.length) return false;

        const lastFieldset = fieldsets[fieldsets.length - 1];
        let input = event.target.closest?.('input[data-option-value-id]');
        if (!input) {
            const label = event.target.closest?.('label[for]');
            if (label) input = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
        }

        return Boolean(input && lastFieldset.contains(input));
    }

    syncUrlFromDom() {
        const variantId = this.getSelectedVariantIdFromDom();
        if (!variantId) return;
        this.updateURLWithVariantId(variantId);
        this.updateVariantInputWithId(variantId);
    }

    updatePriceFromVariant() {
        if (!this.currentVariant?.price) return;

        const priceContainer = document.getElementById(`product-price-${this.dataset.product}`);
        if (!priceContainer) return;

        const formatCents = (cents) => {
            if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
                return Shopify.formatMoney(cents, window.money_format);
            }
            return `$${(cents / 100).toFixed(2)}`;
        };

        const setMoney = (el, cents) => {
            if (!el || cents == null) return;
            const formatted = formatCents(cents);
            if (formatted.includes('<')) {
                el.innerHTML = formatted;
            } else {
                el.textContent = formatted;
            }
        };

        const priceRoot = priceContainer.querySelector('.price');
        const compareAt = this.currentVariant.compare_at_price;
        const onSale = compareAt && compareAt > this.currentVariant.price;

        if (onSale) {
            priceRoot?.classList.add('price--on-sale');
            setMoney(priceContainer.querySelector('.price__sale .price-item--sale'), this.currentVariant.price);
            setMoney(
                priceContainer.querySelector('.price__compare .price-item--regular, .price__compare .price-hello'),
                compareAt
            );
        } else {
            priceRoot?.classList.remove('price--on-sale');
            setMoney(priceContainer.querySelector('.price__regular .price-item--regular'), this.currentVariant.price);
        }

        priceContainer.classList.remove('visibility-hidden');

        if (this.checkNeedToConvertCurrency?.()) {
            const currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
            Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
        }
    }

    updateVariantInput() {
        if (!this.currentVariant?.id) return;
        this.updateVariantInputWithId(this.currentVariant.id);
    }

    updatePickupAvailability() {
        const pickUpAvailability = document.querySelector('pickup-availability');
        if (!pickUpAvailability) return;

        if (this.currentVariant?.available) {
            pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
            pickUpAvailability.removeAttribute('available');
            pickUpAvailability.innerHTML = '';
        }
    }

    renderProductAjaxInfo() {
        fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
                const id = `product-price-${this.dataset.product}`;
                const html = new DOMParser().parseFromString(responseText, 'text/html')
                const destination = document.getElementById(id);
                const source = html.getElementById(id);

                const property = `product-property-${this.dataset.product}`;
                const destinationProperty = document.getElementById(property);
                const sourceProperty = html.getElementById(property);

                if (source && destination) {
                    destination.innerHTML = source.innerHTML;
                }

                if (this.checkNeedToConvertCurrency()) {
                    let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');

                    Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                }

                if(destinationProperty) {
                    if (sourceProperty) {
                        destinationProperty.innerHTML = sourceProperty.innerHTML;
                        destinationProperty.style.display = 'table';
                    } else{
                        destinationProperty.style.display = 'none';
                    }
                } else if (sourceProperty) {
                    document.querySelector('.productView-product').insertBefore(sourceProperty, document.querySelector('.productView-options'));
                }

                document.getElementById(`product-price-${this.dataset.product}`)?.classList.remove('visibility-hidden');
        });
    }

    renderProductInfo() {
        if(this.item.find('[data-sku]').length > 0){
            this.item.find('[data-sku] .productView-info-value').text(this.currentVariant.sku);
        }

        var inventory = this.currentVariant?.inventory_management;

        if(inventory != null) {
            var arrayInVarName = `product_inven_array_${this.dataset.product}`,
                inven_array = window[arrayInVarName];

            if(inven_array != undefined) {
                var inven_num = inven_array[this.currentVariant.id],
                    inventoryQuantity = parseInt(inven_num);

                this.item.find('input[name="quantity"]').attr('data-inventory-quantity', inventoryQuantity);

                if(this.item.find('[data-inventory]').length > 0){
                    if(inventoryQuantity > 0){
                        const showStock = this.item.find('[data-inventory]').data('stock-level');
                        if (showStock == 'show') {
                            this.item.find('[data-inventory] .productView-info-value').text(inventoryQuantity+' '+window.inventory_text.inStock);
                        }
                        else {
                            this.item.find('[data-inventory] .productView-info-value').text(window.inventory_text.inStock);
                        }
                    } else {
                        this.item.find('[data-inventory] .productView-info-value').text(window.inventory_text.outOfStock);
                    }
                }

                hotStock(inventoryQuantity);
            }
        }

        if(this.item.find('.productView-stickyCart').length > 0){
            const sticky = this.item.find('.productView-stickyCart');
            const optionSticky = sticky.find('.select__select');

            optionSticky.val(this.currentVariant.id);
        }
    }

    updateProductInfo() {
        fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
                const description = `[data-product-description-${this.dataset.product}]`
                const html = new DOMParser().parseFromString(responseText, 'text/html')
                const destinationDesc = document.querySelector(description);
                const sourceDesc = html.querySelector(description);

                if (sourceDesc && destinationDesc) {
                    destinationDesc.innerHTML = sourceDesc.innerHTML;
                    if (destinationDesc.closest('.toggle-content--height')){
                        destinationDesc.style.maxHeight = null;
                    }
                }
        });
    }

    updateAttribute(unavailable = true, disable = true){
        const addButton = document.getElementById(`product-form-${this.dataset.product}`)?.querySelector('[name="add"]');
        var quantityInput = this.item.find('input[name="quantity"]'),
            notifyMe = this.item.find('.productView-notifyMe'),
            hotStock = this.item.find('.productView-hotStock'),
            buttonAddtocart = this.item.find('.product-form__submit'),
            maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));

        if (isNaN(maxValue)) {
            maxValue = maxValue = parseInt(buttonAddtocart.attr('data-inventory-quantity'));
        } else {
            maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));
        }
        
        if(unavailable){
            var text = window.variantStrings.unavailable;

            quantityInput.attr('disabled', true);
            notifyMe.slideUp('slow');
            addButton.setAttribute('disabled', true);
            addButton.textContent = text;
            quantityInput.closest('quantity-input').addClass('disabled');

            if(hotStock.length > 0) hotStock.addClass('is-hiden');
        } else {
            if (disable) {
                var text = window.variantStrings.soldOut,
                    subTotal = 0,
                    price = this.currentVariant?.price;

                const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');
                const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');

                if (window.subtotal.show) {
                    let qty = quantityInput.val();

                    subTotal = qty * price;
                    subTotal = Shopify.formatMoney(subTotal, window.money_format);
                    subTotal = extractContent(subTotal);

                    const moneySpan = document.createElement('span')
                    moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal')
                    moneySpan.innerText = subTotal
                    document.body.appendChild(moneySpan)

                    if (this.checkNeedToConvertCurrency()) {
                        let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                        Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                    }

                    subTotal = moneySpan.innerText
                    $(moneySpan).remove()

                    if (window.subtotal.style == '1') {
                        const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');
                        if (pdView_subTotal != null) {
                            pdView_subTotal.textContent = subTotal;
                        }
                    } else if (window.subtotal.style == '2') {
                        text = window.subtotal.text.replace('[value]', subTotal);
                    }
                } else {
                    subTotal = Shopify.formatMoney(price, window.money_format);
                    subTotal = extractContent(subTotal);
                }

                quantityInput.attr('data-price', this.currentVariant?.price);
                quantityInput.attr('disabled', true);
                addButton.setAttribute('disabled', true);
                addButton.textContent = text;
                quantityInput.closest('quantity-input').addClass('disabled');

                if (subTotal != 0 && stickyPrice.length) {
                    stickyPrice.text(subTotal);
                }

                const thisStickyPrice = $('[data-sticky-add-to-cart] .sticky-price');
                const thisComparePrice = $('[data-sticky-add-to-cart] .money-compare-price');
                const compare_at_price = this.currentVariant?.compare_at_price;

                if (compare_at_price) {
                    thisStickyPrice.addClass('has-compare-price');
                    if (thisComparePrice.length) {
                        thisComparePrice.attr('data-compare-price', compare_at_price);
                    } else {
                        thisStickyPrice.prepend(`<s class="money-compare-price" data-compare-price="${compare_at_price}"><span class="money"></span></s>`);
                    }
                } else {
                    thisStickyPrice.removeClass('has-compare-price');
                    thisComparePrice.remove();
                }

                if (subTotal != 0 && stickyComparePrice.length && window.subtotal.show) {
                    let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price'),
                        qty = quantityInput.val();
                    comparePrice = qty * comparePrice;
                    comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                    comparePrice = extractContent(comparePrice);
                    stickyComparePrice.text(comparePrice);
                }

                if (notifyMe.length > 0) {
                    notifyMe.find('.halo-notify-product-variant').val(this.currentVariant.title);
                    notifyMe.find('.notifyMe-text').empty();
                    notifyMe.slideDown('slow');
                }
            } else {
                var text,
                    subTotal = 0,
                    price = this.currentVariant?.price;

                const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');

                if (window.subtotal.show) {
                    let qty = quantityInput.val();

                    subTotal = qty * price;
                    subTotal = Shopify.formatMoney(subTotal, window.money_format);
                    subTotal = extractContent(subTotal);

                    const moneySpan = document.createElement('span')
                    moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal')
                    moneySpan.innerText = subTotal
                    document.body.appendChild(moneySpan)

                    if (this.checkNeedToConvertCurrency()) {
                        let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                        Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                    }

                    subTotal = moneySpan.innerText
                    $(moneySpan).remove()

                    if (window.subtotal.style == '1') {
                        const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');
                        if (pdView_subTotal != null) {
                            pdView_subTotal.textContent = subTotal;
                        }

                        if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                            text = window.variantStrings.preOrder;
                        } else {
                            text = window.variantStrings.addToCart;
                        }
                    } else if (window.subtotal.style == '2') {
                        text = window.subtotal.text.replace('[value]', subTotal);
                    }
                } else {
                    subTotal = Shopify.formatMoney(price, window.money_format);
                    subTotal = extractContent(subTotal);
                    if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                        text = window.variantStrings.preOrder;
                    } else {
                        text = window.variantStrings.addToCart;
                    }
                }

                quantityInput.attr('data-price', this.currentVariant?.price);
                quantityInput.attr('disabled', false);
                addButton.removeAttribute('disabled');
                addButton.textContent = text;
                quantityInput.closest('quantity-input').removeClass('disabled');

                if (subTotal != 0 && stickyPrice.length) {
                    stickyPrice.text(subTotal);
                }

                const thisStickyPrice = $('[data-sticky-add-to-cart] .sticky-price');
                const thisComparePrice = $('[data-sticky-add-to-cart] .money-compare-price');
                const compare_at_price = this.currentVariant?.compare_at_price;

                if (compare_at_price) {
                    thisStickyPrice.addClass('has-compare-price');
                    if (thisComparePrice.length) {
                        thisComparePrice.attr('data-compare-price', compare_at_price);
                    } else {
                        thisStickyPrice.prepend(`<s class="money-compare-price" data-compare-price="${compare_at_price}"><span class="money"></span></s>`);
                    }
                } else {
                    thisStickyPrice.removeClass('has-compare-price');
                    thisComparePrice.remove();
                }

                const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');
                if (subTotal != 0 && stickyComparePrice.length && window.subtotal.show) {
                    let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price'),
                        qty = quantityInput.val();
                    comparePrice = qty * comparePrice;
                    comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                    comparePrice = extractContent(comparePrice);
                    stickyComparePrice.text(comparePrice);
                }

                if (notifyMe.length > 0) {
                    notifyMe.slideUp('slow');
                }
            }
        }
    }

    updateStickyAddToCart(unavailable = true, disable = true){
        if(this.item.find('.productView-stickyCart').length > 0){
            const sticky = this.item.find('.productView-stickyCart');
            const itemImage = sticky.find('.sticky-image');
            const option = sticky.find('.select__select');
            const input = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('input[name="id"]');
            const button = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('[name="add"]');
            var quantityInput = this.item.find('input[name="quantity"]');
            var maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));
          
            if(unavailable){
                var text = window.variantStrings.unavailable;

                button.setAttribute('disabled', true);
                button.textContent = text;
            } else {
                if (!this.currentVariant) return;

                const image = this.currentVariant?.featured_image;
                
                if (image != null) {
                    itemImage.find('img').attr({
                        'src': image.src,
                        'srcset': image.src,
                        'alt': image.alt
                    });
                }

                option.val(this.currentVariant.id);

                if (disable) {
                    var text = window.variantStrings.soldOut;

                    button.setAttribute('disabled', true);
                    button.textContent = text;
                } else {
                    if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                        text = window.variantStrings.preOrder;
                    } else {
                        text = window.variantStrings.addToCart;
                    }

                    button.removeAttribute('disabled');
                    button.textContent = text;
                }

                input.value = this.currentVariant.id;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    getVariantData() {
        return this.variantData || this.getVariantDataSync();
    }

    checkNeedToConvertCurrency() {
        var currencyItem = $('.dropdown-item[data-currency]');
        if (currencyItem.length) {
            return (window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency;
        } else {
            return;
        }
    }

    checkQuantityWhenVariantChange() {
        var quantityInput = this.closest('.productView-details').querySelector('input.quantity__input')
        var maxValue = parseInt(quantityInput?.dataset.inventoryQuantity);
        var inputValue = parseInt(quantityInput?.value);

        let value = inputValue 

        if (inputValue > maxValue && maxValue > 0) {
            value = maxValue
        } else {
            value = inputValue
        }

        if (value < 1 || isNaN(value)) value = 1 
      
        if (quantityInput) {
          quantityInput.value = value
        }
      
        document.getElementById('product-add-to-cart').dataset.available = this.currentVariant.available && maxValue <= 0
    }

    updateVariantStatuses() {
        // const options = this.item.find('.productView-details .product-form__input'),
        //     optionsLength = options.length,
        //     pvOptionsLength = PVoptions.length,
        //     checkStickyVariant = false;
      
        // optionsLength > pvOptionsLength ? checkStickyVariant = true : '';

        const container = this.querySelector('.optionValueSelectors');
        const firstChecked = container?.querySelector('fieldset input[type="radio"]:checked');
        const selectedColor = firstChecked ? this.decodeOption(firstChecked.value) : null;
        const selectedOptionOneVariants = this.variantData.filter((variant) => (
            selectedColor ? selectedColor === variant.option1 : true
        ));
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        const inputLength = inputWrappers.length;
        inputWrappers.forEach((option, index) => {
            const checkedInput = option.querySelector(':checked');
            if (checkedInput) {
                option.querySelector('[data-header-option]').innerText = this.decodeOption(checkedInput.value);
            }
            if (index === 0 && inputLength > 1) return;
            const optionInputs = [...option.querySelectorAll('input[type="radio"], option')]
            const previousOptionSelected = inputLength > 1
                ? this.decodeOption(inputWrappers[index - 1].querySelector(':checked').value)
                : this.decodeOption(inputWrappers[index].querySelector(':checked').value);
            const optionInputsValue = inputLength > 1 ? selectedOptionOneVariants.filter(variant => variant[`option${ index }`] === previousOptionSelected).map(variantOption => variantOption[`option${ index + 1 }`]) : this.variantData.map(variantOption => variantOption[`option${ index + 1 }`]);
            const availableOptionInputsValue = inputLength > 1 ? selectedOptionOneVariants.filter(variant => variant.available && variant[`option${ index }`] === previousOptionSelected).map(variantOption => variantOption[`option${ index + 1 }`]) : this.variantData.filter(variant => variant.available).map(variantOption => variantOption[`option${ index + 1 }`]);
            this.setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue)
        });
    }

    setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue) {
        optionInputs.forEach(input => {
            const inputValue = this.decodeOption(input.getAttribute('value'));
            if (availableOptionInputsValue.includes(inputValue)) {
                input.classList.remove('soldout');
                input.innerText = inputValue;
            } else {
                input.classList.add('soldout');
                optionInputsValue.includes(inputValue) ? input.innerText = inputValue + ' (Sold out)' : input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', inputValue)
            }
        });
    }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
    constructor() {
        super();
    }

    setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue) {
        optionInputs.forEach(input => {
            const inputValue = this.decodeOption(input.getAttribute('value'));
            if (availableOptionInputsValue.includes(inputValue)) {
                input.nextSibling.classList.remove('soldout', 'unavailable');
                input.nextSibling.classList.add('available');
            } else {
                input.nextSibling.classList.remove('available', 'unavailable');
                input.nextSibling.classList.add('soldout');

                if (window.variantStrings.hide_variants_unavailable && !optionInputsValue.includes(inputValue)) {
                    input.nextSibling.classList.add('unavailable')
                    if (!input.checked) return;
                    let inputsValue;
                    availableOptionInputsValue.length > 0 ? inputsValue = availableOptionInputsValue : inputsValue = optionInputsValue;
                    const fallbackInput = Array.from(input.closest('.product-form__input')?.querySelectorAll('input[type="radio"]') || [])
                        .find((radio) => this.decodeOption(radio.getAttribute('value')) === inputsValue[0]);
                    if (fallbackInput) {
                        fallbackInput.checked = true;
                        this.dispatchEvent(new Event('change'))
                    }
                }
            }
        });
    }
        
    updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.options = fieldsets.map((fieldset) => {
            return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
        });
    }
}

customElements.define('variant-radios', VariantRadios);

class QuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));

        this.querySelectorAll('button').forEach(
            (button) => button.addEventListener('click', this.onButtonClick.bind(this))
        );

        if (!this.checkHasMultipleVariants()) {
            this.initProductQuantity();
            this.checkVariantInventory();
        }
    }

    onInputChange(event) {
        event.preventDefault(); 
        var inputValue = this.input.value;
        var maxValue = parseInt(this.input.dataset.inventoryQuantity);
        var currentId = document.getElementById(`product-form-${this.input.dataset.product}`)?.querySelector('[name="id"]')?.value;
        var saleOutStock  = document.getElementById('product-add-to-cart').dataset.available === 'true' || false ;
        const addButton = document.getElementById(`product-form-${this.input.dataset.product}`)?.querySelector('[name="add"]');

        if(inputValue < 1) {
            inputValue = 1;

            this.input.value =  inputValue;
        }
        
        if (inputValue > maxValue && !saleOutStock && maxValue > 0) {
            var arrayInVarName = `selling_array_${this.input.dataset.product}`,
                itemInArray = window[arrayInVarName],
                itemStatus = itemInArray[currentId];
          
            if(itemStatus == 'deny') {
              inputValue = maxValue
              this.input.value = inputValue;
              const message = getInputMessage(maxValue)
              showWarning(message, 3000)
            }
        } else if (inputValue > maxValue && saleOutStock && maxValue <= 0) {
            this.input.value = inputValue;
        }

        if(window.subtotal.show) {
            var text,
                price = this.input.dataset.price,
                subTotal = 0;

            var parser = new DOMParser();

            subTotal = inputValue * price;
            subTotal = Shopify.formatMoney(subTotal, window.money_format);
            subTotal = extractContent(subTotal);

            const moneySpan = document.createElement('span')
            moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal') 
            moneySpan.innerText = subTotal 
            document.body.appendChild(moneySpan) 

            if (this.checkNeedToConvertCurrency()) {
                let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
            }

            subTotal = moneySpan.innerText 
            $(moneySpan).remove()

            if (window.subtotal.style == '1') {
                const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');

                pdView_subTotal.textContent = subTotal;
            }
            else if (window.subtotal.style == '2') {
                text = window.subtotal.text.replace('[value]', subTotal);

                addButton.textContent = text;  
            }

            const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');
            const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');

            if (stickyPrice.length) {
                stickyPrice.text(subTotal);
            }

            if (stickyComparePrice.length && window.subtotal.show) {
                let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price');
                comparePrice = inputValue * comparePrice;
                comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                comparePrice = extractContent(comparePrice);
                stickyComparePrice.text(comparePrice);
            }
        }

        if (this.classList.contains('quantity__group--2') || this.classList.contains('quantity__group--3')) {
            const mainQty = document.querySelector('.quantity__group--1 .quantity__input');
            mainQty.value = inputValue;

            const mainQty2Exists = !!document.querySelector('.quantity__group--2 .quantity__input');
            const mainQty3Exists = !!document.querySelector('.quantity__group--3 .quantity__input');

            if (this.classList.contains('quantity__group--2') && mainQty3Exists) {
                const mainQty3 = document.querySelector('.quantity__group--3 .quantity__input');
                mainQty3.value = inputValue;
            }
            else if (this.classList.contains('quantity__group--3') && mainQty2Exists) {
                const mainQty2 = document.querySelector('.quantity__group--2 .quantity__input');
                mainQty2.value = inputValue;
            }
        }
    }

    onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
        
        event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    checkNeedToConvertCurrency() {
        return (window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency;
    }
    
    checkHasMultipleVariants() {
        const arrayInVarName = `product_inven_array_${this.querySelector('[data-product]').dataset.product}`
        this.inven_array = window[arrayInVarName];
        return Object.keys(this.inven_array).length > 1
    }
        
    initProductQuantity() {
        if(this.inven_array != undefined) {
            var inven_num = Object.values(this.inven_array),
                inventoryQuantity = parseInt(inven_num);

            this.querySelector('input[name="quantity"]').setAttribute('data-inventory-quantity', inventoryQuantity);
            this.querySelector('input[name="quantity"]').dataset.inventoryQuantity = inventoryQuantity
        }
    }

    checkVariantInventory() {
        const addBtn = document.getElementById('product-add-to-cart')
        this.input.disabled = addBtn.disabled 
        this.querySelector('.btn-quantity.minus').disabled = addBtn.disabled
        this.querySelector('.btn-quantity.plus').disabled = addBtn.disabled
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(document.querySelector('.product-option [type="application/json"]').textContent);
        return this.variantData;
    }
}

customElements.define('quantity-input', QuantityInput);

function hotStock(inventoryQuantity) {
    const productView = document.querySelector('.productView');
    const hotStock = productView.querySelector('.productView-hotStock');
    if(hotStock) {
        let hotStockText = hotStock.querySelector('.hotStock-text'),
            maxStock = parseFloat(hotStock.dataset.hotStock),
            textStock;

        if(inventoryQuantity > 0 && inventoryQuantity <= maxStock){
            hotStock.matches('.style-2') ? textStock  = window.inventory_text.hotStock2.replace('[inventory]', inventoryQuantity) : textStock  = window.inventory_text.hotStock.replace('[inventory]', inventoryQuantity);
            if (hotStockText) hotStockText.innerHTML = textStock;
            hotStock.classList.remove('is-hide');
        } else {
            hotStock.classList.add('is-hide');
        }

        if (hotStock.matches('.style-2')) {
            const invenProgress = inventoryQuantity / maxStock * 100,
                hotStockProgressItem = hotStock.querySelector('.hotStock-progress-item');
            if (hotStockProgressItem) hotStockProgressItem.style.width = `${invenProgress}%`;
        }
    }
}
const hotStockNoOptions = document.querySelector('.productView .productView-hotStock[data-current-inventory]');
if (hotStockNoOptions) {
    const inventoryQuantity = parseFloat(hotStockNoOptions.dataset.currentInventory);
    hotStock(inventoryQuantity);
}


function showWarning(content, time = null) {
    if (window.warningTimeout) {
        clearTimeout(window.warningTimeout);
    }
    const warningPopupContent = document.getElementById('halo-warning-popup').querySelector('[data-halo-warning-content]')
    warningPopupContent.textContent = content
    document.body.classList.add('has-warning')

    if (time) {
        window.warningTimeout = setTimeout(() => {
            document.body.classList.remove('has-warning')
        }, time)
    }
}

function getInputMessage(maxValue) {
    var message = window.cartStrings.addProductOutQuantity.replace('[maxQuantity]', maxValue);
    return message
}